import {
  forwardRef, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  type InputHTMLAttributes, type KeyboardEvent, type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { Champ, type ProprietesChamp } from './composants.tsx';

/**
 * Liste de propositions sous un champ de saisie libre.
 *
 * ── Pourquoi pas `<datalist>` ───────────────────────────────────────────────
 * Signalé dans le cahier journal : « les sélections des menus déroulants sont
 * des bulles noires ». `<datalist>` est dessinée par le navigateur, à sa façon
 * et hors du thème : une bulle sombre qui ne suit ni la largeur ni la position
 * du champ. On ne peut ni la styler ni la placer.
 *
 * ── Collée au champ, et jamais rognée ───────────────────────────────────────
 * Le champ vit souvent dans une zone qui défile — le tableau du journal — et
 * une liste positionnée dedans y serait coupée net. Elle est donc posée en
 * `position: fixed` à l'endroit exact du champ, recalculé à chaque défilement
 * et redimensionnement. Dans une boîte de dialogue, elle est rendue DANS la
 * boîte : hors d'elle, la couche modale la recouvrirait.
 *
 * ── Non contraignante ───────────────────────────────────────────────────────
 * On écrit ce qu'on veut : la liste propose, elle n'impose pas. Flèches pour
 * parcourir, Entrée pour choisir, Échap pour refermer.
 */

/** Insensible à la casse ET aux accents : « francais » trouve « Français ». */
const normaliser = (s: string) =>
  s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

/** Au-delà, la liste cesse d'aider : on tape une lettre de plus. */
const MAXIMUM = 50;

/** Les propositions qui contiennent le texte saisi, sans celle déjà écrite. */
export function filtrerPropositions(propositions: string[], texte: string): string[] {
  const t = normaliser(texte);
  return propositions
    .filter((p) => normaliser(p) !== t && (!t || normaliser(p).includes(t)))
    .slice(0, MAXIMUM);
}

interface Position { gauche: number; haut: number; largeur: number; }

/**
 * L'origine et l'échelle réelles de `position: fixed` pour la liste.
 *
 * D'ordinaire l'écran lui-même : origine 0, échelle 1. Mais un ancêtre
 * transformé ou zoomé devient le repère des éléments fixes — c'est le cas de
 * la boîte de dialogue, mesuré décalée de 254 px — et le zoom de l'affichage
 * change l'échelle. Plutôt que de deviner lequel s'applique, la liste se
 * MESURE après s'être posée, et en déduit le repère.
 */
interface Repere { x: number; y: number; echelle: number; }

/**
 * L'état d'une liste de propositions : ouverture, élément actif, clavier.
 *
 * Partagé par `SaisieDifferee` et `ChampPropositions`, qui n'ont pas le même
 * champ mais le même comportement.
 */
export function usePropositions({
  propositions, texte, surChoix,
}: {
  propositions: string[];
  texte: string;
  surChoix: (valeur: string) => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [actif, setActif] = useState(-1);
  const idListe = useId();
  const visibles = useMemo(() => filtrerPropositions(propositions, texte), [propositions, texte]);
  const affiche = ouvert && visibles.length > 0;

  useEffect(() => { setActif(-1); }, [texte]);

  const choisir = useCallback((valeur: string) => {
    setOuvert(false);
    setActif(-1);
    surChoix(valeur);
  }, [surChoix]);

  function surTouche(e: KeyboardEvent<HTMLElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOuvert(true);
      setActif((a) => Math.min(a + 1, visibles.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActif((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && affiche && actif >= 0) {
      e.preventDefault();
      choisir(visibles[actif]!);
    } else if (e.key === 'Escape' && affiche) {
      // Échap referme la liste, et SEULEMENT elle : pas la boîte de dialogue.
      e.preventDefault();
      e.stopPropagation();
      setOuvert(false);
    }
  }

  return {
    visibles, affiche, actif, idListe, choisir,
    ouvrir: () => setOuvert(true),
    fermer: () => setOuvert(false),
    surTouche,
    /** Les attributs ARIA du champ : un lecteur d'écran annonce la liste. */
    attributsChamp: {
      role: 'combobox' as const,
      'aria-autocomplete': 'list' as const,
      'aria-expanded': affiche,
      'aria-controls': idListe,
      ...(affiche && actif >= 0 ? { 'aria-activedescendant': `${idListe}-${actif}` } : {}),
    },
  };
}

/** La liste elle-même, posée sous le champ désigné par `ancre`. */
export function ListePropositions({
  ancre, visibles, affiche, actif, idListe, surChoix,
}: {
  ancre: RefObject<HTMLElement | null>;
  visibles: string[];
  affiche: boolean;
  actif: number;
  idListe: string;
  surChoix: (valeur: string) => void;
}) {
  const [position, setPosition] = useState<Position | null>(null);
  const [repere, setRepere] = useState<Repere>({ x: 0, y: 0, echelle: 1 });
  const [cible, setCible] = useState<HTMLElement | null>(null);
  const liste = useRef<HTMLUListElement>(null);

  const placer = useCallback(() => {
    const el = ancre.current;
    if (!el) return;
    const conteneur = (el.closest('dialog') as HTMLElement | null) ?? document.body;
    const r = el.getBoundingClientRect();
    setCible(conteneur);
    setPosition({ gauche: r.left, haut: r.bottom + 2, largeur: Math.max(r.width, 176) });
  }, [ancre]);

  useLayoutEffect(() => {
    if (!affiche) return;
    placer();
    let image = 0;
    const suivre = () => {
      cancelAnimationFrame(image);
      image = requestAnimationFrame(placer);
    };
    // `capture` : le défilement d'une zone intérieure — le tableau — ne
    // remonte pas jusqu'à la fenêtre.
    window.addEventListener('scroll', suivre, true);
    window.addEventListener('resize', suivre);
    // L'animation d'ouverture d'une boîte de dialogue la transforme, et en fait
    // le repère des éléments fixes le temps qu'elle dure : on se recale à la fin.
    window.addEventListener('animationend', suivre, true);
    return () => {
      cancelAnimationFrame(image);
      window.removeEventListener('scroll', suivre, true);
      window.removeEventListener('resize', suivre);
      window.removeEventListener('animationend', suivre, true);
    };
  }, [affiche, placer]);

  // Les coordonnées posées, dans le repère supposé.
  const style = position ? {
    left: (position.gauche - repere.x) / repere.echelle,
    top: (position.haut - repere.y) / repere.echelle,
    width: position.largeur / repere.echelle,
  } : null;

  /*
   * Après la pose : où la liste est-elle VRAIMENT ? L'écart donne le repère
   * réel, qu'on adopte s'il diffère — une passe suffit, le repère ne bouge pas
   * d'une image à l'autre.
   */
  useLayoutEffect(() => {
    const el = liste.current;
    if (!el || !style || style.width === 0) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0) return;
    const echelle = r.width / style.width;
    const x = r.left - style.left * echelle;
    const y = r.top - style.top * echelle;
    if (Math.abs(x - repere.x) > 0.5 || Math.abs(y - repere.y) > 0.5
      || Math.abs(echelle - repere.echelle) > 0.005) {
      setRepere({ x, y, echelle });
    }
  });

  // L'élément actif reste visible quand on descend au clavier.
  useEffect(() => {
    if (actif < 0) return;
    liste.current?.children[actif]?.scrollIntoView?.({ block: 'nearest' });
  }, [actif]);

  if (!affiche || !style || !cible) return null;

  return createPortal(
    <ul
      ref={liste}
      id={idListe}
      role="listbox"
      style={{ position: 'fixed', ...style }}
      className={[
        'z-50 max-h-60 overflow-y-auto rounded-lg border p-1',
        'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-fg)]',
        'shadow-[var(--ombre-flottante)]',
      ].join(' ')}
    >
      {visibles.map((p, i) => (
        <li
          key={p}
          id={`${idListe}-${i}`}
          role="option"
          aria-selected={i === actif}
          // `mousedown` empêché : le champ garde le focus, et la liste n'est
          // pas refermée par sa perte avant que le clic ne soit reçu.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => surChoix(p)}
          className={[
            'cursor-pointer rounded-md px-2.5 py-1.5 text-[13px] leading-snug',
            i === actif
              ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary-soft-fg)]'
              : 'hover:bg-[var(--color-surface-hover)]',
          ].join(' ')}
        >
          {p}
        </li>
      ))}
    </ul>,
    cible,
  );
}

/**
 * Un `Champ` muni de propositions — pour les formulaires.
 *
 * Contrôlé comme un champ ordinaire : `value` et `onChange`. Choisir une
 * proposition appelle `surChoix`, ou à défaut `onChange` avec la valeur.
 */
export const ChampPropositions = forwardRef<HTMLInputElement, ProprietesChamp & {
  propositions: string[];
  value: string;
  surChoix?: (valeur: string) => void;
}>(function ChampPropositions({ propositions, value, surChoix, onChange, onKeyDown, onFocus, onBlur, ...reste }, refExterne) {
  const interne = useRef<HTMLInputElement>(null);
  const liste = usePropositions({
    propositions,
    texte: value,
    surChoix: (v) => {
      if (surChoix) surChoix(v);
      else onChange?.({ target: { value: v } } as never);
    },
  });

  const attacher = (el: HTMLInputElement | null) => {
    interne.current = el;
    if (typeof refExterne === 'function') refExterne(el);
    else if (refExterne) refExterne.current = el;
  };

  return (
    <>
      <Champ
        ref={attacher}
        value={value}
        autoComplete="off"
        {...liste.attributsChamp}
        onChange={(e) => { onChange?.(e); liste.ouvrir(); }}
        onFocus={(e) => { liste.ouvrir(); onFocus?.(e); }}
        onBlur={(e) => { liste.fermer(); onBlur?.(e); }}
        onKeyDown={(e) => { liste.surTouche(e); onKeyDown?.(e); }}
        {...(reste as InputHTMLAttributes<HTMLInputElement> & ProprietesChamp)}
      />
      <ListePropositions
        ancre={interne}
        visibles={liste.visibles}
        affiche={liste.affiche}
        actif={liste.actif}
        idListe={liste.idListe}
        surChoix={liste.choisir}
      />
    </>
  );
});
